/**
 * Fixed-length cycle discovery: repeating vertical lines every N bars from an anchor.
 *
 *        -2N      -1N     anchor     +1N      +2N
 *         |        |        |         |        |
 *   ------+--------+--------+---------+--------+------>
 *                          B0                   future space
 *
 * The anchor is a bar the user picks; lines march out in both directions at a fixed interval, so a
 * suspected cycle can be laid over the chart and checked against what price actually did on each
 * side of it.
 */

/** Units a cycle length can be expressed in. */
export const CYCLE_UNITS = { BARS: 'bars', DAYS: 'days' };

/** Adds calendar days to a yyyy-MM-dd string. */
export function addDays(dateStr, n) {
  if (typeof dateStr !== 'string') return null;
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  // Noon UTC, so a daylight-saving shift can never move the result onto the adjacent day. Month
  // lengths and leap years are handled by Date itself rather than by arithmetic here.
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  dt.setUTCDate(dt.getUTCDate() + n);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

/** Whole calendar days between two yyyy-MM-dd strings. */
export function daysBetween(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') return 0;
  const toUTC = (s) => {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    return Number.isFinite(y) ? Date.UTC(y, m - 1, d, 12) : NaN;
  };
  const t1 = toUTC(a);
  const t2 = toUTC(b);
  if (!Number.isFinite(t1) || !Number.isFinite(t2)) return 0;
  return Math.round((t2 - t1) / 86400000);
}

/** Calendar days the chart spans, first bar to last. */
export function totalCalendarDays(dates) {
  if (!Array.isArray(dates) || dates.length < 2) return 0;
  return Math.abs(daysBetween(dates[0], dates[dates.length - 1]));
}

/**
 * Bar index for a calendar date: exact match, else the nearest PRECEDING bar.
 *
 * Preceding rather than nearest-either-way, because a cycle date landing on a weekend or holiday
 * has already arrived by the time the next bar prints — snapping forward would place the line
 * after the date it represents. Returns -1 when the date is before the chart begins.
 *
 * The chart's padded future space holds real weekday dates, so future targets resolve through the
 * same lookup as historical ones; no average-spacing estimate is needed, and the line lands on the
 * bar that actually carries that date rather than near it.
 */
export function barIndexForDate(dates, target) {
  if (!Array.isArray(dates) || dates.length === 0 || typeof target !== 'string') return -1;
  const key = target.slice(0, 10);
  let best = -1;
  // Dates are ascending, so the last one at or before the target is the answer.
  for (let i = 0; i < dates.length; i++) {
    const d = (dates[i] || '').slice(0, 10);
    if (d <= key) best = i; else break;
  }
  return best;
}

/** Shortest cycle worth looking for. Below this the lines are dense enough to fit any chart. */
export const MIN_CYCLE_BARS = 10;

/**
 * Longest usable cycle, as a fraction of the chart.
 *
 * Half the bars, so a cycle can repeat at least once within the data. A longer one draws an anchor
 * and almost nothing else, which cannot be assessed: there is no repetition to look at.
 */
export const MAX_CYCLE_FRACTION = 0.5;

/** The largest N this chart can support. */
export function maxCycleFor(totalBars) {
  if (typeof totalBars !== 'number' || !Number.isFinite(totalBars)) return 0;
  return Math.floor(totalBars * MAX_CYCLE_FRACTION);
}

/**
 * Validates a cycle length against the chart.
 *
 * @returns {{valid:boolean, value:number|null, error:string|null}}
 */
export function validateCycleLength(input, totalBars, { unit = CYCLE_UNITS.BARS, totalDays = 0 } = {}) {
  const isDays = unit === CYCLE_UNITS.DAYS;
  const max = isDays ? maxCycleFor(totalDays) : maxCycleFor(totalBars);
  const noun = isDays ? 'calendar days' : 'bars';

  if (max < MIN_CYCLE_BARS) {
    return { valid: false, value: null, error: `This chart is too short for cycle analysis (needs at least ${MIN_CYCLE_BARS * 2} ${noun}).` };
  }

  // Rejected as text before Number() sees it: Number('') is 0 and Number(' ') is 0, so an empty
  // box would otherwise read as a valid-looking zero rather than as nothing entered.
  const raw = typeof input === 'string' ? input.trim() : input;
  if (raw === '' || raw === null || raw === undefined) {
    return { valid: false, value: null, error: `Enter a cycle length in ${noun}.` };
  }

  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return { valid: false, value: null, error: 'Cycle length must be a number.' };
  }
  if (!Number.isInteger(n)) {
    return { valid: false, value: null, error: `Cycle length must be a whole number of ${noun}.` };
  }
  if (n < MIN_CYCLE_BARS) {
    return { valid: false, value: null, error: `Cycle length must be at least ${MIN_CYCLE_BARS} ${noun}.` };
  }
  if (n > max) {
    return { valid: false, value: null, error: `Cycle length must be at most ${max} ${noun} (half the chart).` };
  }
  return { valid: true, value: n, error: null };
}

/**
 * Bar indices of every cycle line, anchor included, ascending.
 *
 * Backward lines stop at index 0 and forward lines at maxIndex, which is the last ADDRESSABLE bar
 * rather than the last bar with price — so a cycle continues into the chart's padded future space
 * instead of stopping dead at today, which is the half of the projection worth seeing.
 *
 * @param {number} anchorIndex
 * @param {number} n         cycle length in bars
 * @param {number} maxIndex  last addressable bar, including padded space
 * @returns {Array<{index:number, k:number}>} k is the cycle number: negative back, positive forward
 */
export function cycleIndices(anchorIndex, n, maxIndex) {
  const usable = (v) => typeof v === 'number' && Number.isFinite(v);
  if (!usable(anchorIndex) || !usable(n) || !usable(maxIndex)) return [];
  if (n < 1 || anchorIndex < 0 || anchorIndex > maxIndex) return [];

  const out = [{ index: anchorIndex, k: 0 }];

  for (let k = 1; ; k++) {
    const idx = anchorIndex - k * n;
    if (idx < 0) break;
    out.push({ index: idx, k: -k });
  }
  for (let k = 1; ; k++) {
    const idx = anchorIndex + k * n;
    if (idx > maxIndex) break;
    out.push({ index: idx, k });
  }

  return out.sort((a, b) => a.index - b.index);
}

/**
 * Cycle lines by CALENDAR interval rather than bar count.
 *
 * Each multiple is computed as a date first and then resolved to a bar, so the spacing stays true
 * to the calendar across weekends, holidays and leap years. Doing it as a bar count instead would
 * drift: 90 trading bars is roughly 126 calendar days, and the error compounds with every cycle.
 *
 * Two lines can resolve to the same bar when the interval is short enough that consecutive targets
 * fall inside one weekend. Duplicates are dropped, keeping the nearer multiple, since two lines on
 * one bar are indistinguishable and the badge would be a coin toss.
 *
 * @returns {Array<{index:number, k:number, targetDate:string}>} ascending by index
 */
export function cycleIndicesByDays(anchorIndex, n, dates) {
  const usable = (v) => typeof v === 'number' && Number.isFinite(v);
  if (!Array.isArray(dates) || dates.length === 0) return [];
  if (!usable(anchorIndex) || !usable(n) || n < 1) return [];
  if (anchorIndex < 0 || anchorIndex >= dates.length) return [];

  const anchorDate = (dates[anchorIndex] || '').slice(0, 10);
  if (!anchorDate) return [];

  const firstDate = (dates[0] || '').slice(0, 10);
  const lastDate = (dates[dates.length - 1] || '').slice(0, 10);

  const byIndex = new Map();
  const add = (index, k, targetDate) => {
    if (index < 0 || index >= dates.length) return;
    const seen = byIndex.get(index);
    if (!seen || Math.abs(k) < Math.abs(seen.k)) byIndex.set(index, { index, k, targetDate });
  };

  add(anchorIndex, 0, anchorDate);

  for (let k = 1; k < 1000; k++) {
    const target = addDays(anchorDate, -k * n);
    if (!target || target < firstDate) break;
    add(barIndexForDate(dates, target), -k, target);
  }
  for (let k = 1; k < 1000; k++) {
    const target = addDays(anchorDate, k * n);
    // Past the end of the chart, including its padded future space: no bar to draw on.
    if (!target || target > lastDate) break;
    add(barIndexForDate(dates, target), k, target);
  }

  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}

/** Badge for one cycle line: "Anchor (0)", "+2N (+60b)", "-1N (-30d)". */
export function cycleLabel(k, n, unit = CYCLE_UNITS.BARS) {
  if (k === 0) return 'Anchor (0)';
  const sign = k > 0 ? '+' : '-';
  const suffix = unit === CYCLE_UNITS.DAYS ? 'd' : 'b';
  return `${sign}${Math.abs(k)}N (${sign}${Math.abs(k) * n}${suffix})`;
}

/**
 * The overlays for one fixed-cycle drawing.
 *
 * Every line shares a groupId, so the cycle deletes and undoes as one figure rather than as
 * however many verticals happened to fit on the chart.
 *
 * @param {number}   anchorIndex
 * @param {number}   n
 * @param {string[]} dates          chart dates, including padded future space
 * @param {object}   [opts]         {color, groupId, lastRealIndex}
 */
export function buildFixedCycleOverlays(anchorIndex, n, dates, { color = '#8e24aa', groupId, lastRealIndex = null, unit = CYCLE_UNITS.BARS } = {}) {
  if (!Array.isArray(dates) || dates.length === 0) return [];
  const isDays = unit === CYCLE_UNITS.DAYS;
  const lines = isDays
    ? cycleIndicesByDays(anchorIndex, n, dates)
    : cycleIndices(anchorIndex, n, dates.length - 1);
  if (lines.length === 0) return [];

  const gid = groupId || `fixedcycle-${anchorIndex}-${n}`;

  return lines.map(({ index, k, targetDate }) => ({
    type: 'vLine',
    date: dates[index],
    color,
    // The anchor is solid so the bar everything is measured from is identifiable at a glance;
    // the repeats are dashed because they are derived, not chosen.
    lineType: k === 0 ? 'solid' : 'dashed',
    thickness: k === 0 ? 2 : 1,
    // In days mode the target date is shown too: the line sits on the nearest preceding TRADING
    // bar, which is often not the calendar date the cycle actually names, and the badge would
    // otherwise imply the two are the same.
    label: isDays && k !== 0 && targetDate
      ? `${cycleLabel(k, n, unit)}
${targetDate}`
      : cycleLabel(k, n, unit),
    targetDate: targetDate || dates[index],
    unit,
    isFuture: lastRealIndex !== null && index > lastRealIndex,
    cycleK: k,
    groupId: gid,
    id: `${gid}-k${k}`,
  }));
}

/** Marks overlays produced by this tool, so a cycle can be found and replaced. */
export const FIXED_CYCLE_PREFIX = 'fixedcycle-';

/** The overlay list with every fixed-cycle drawing removed. */
export function withoutFixedCycles(overlays) {
  if (!Array.isArray(overlays)) return [];
  return overlays.filter(o => !(o && typeof o.id === 'string' && o.id.startsWith(FIXED_CYCLE_PREFIX)));
}
